import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthorizeWebResponse, CustomerListDto, CustomerWithTimezoneListDto } from "../api/types";
import { useTranslation } from "react-i18next";
import { ColumnMeta } from "../components/ColumnChooser";
import { resolveLanguage } from "../i18n";
import { useFavourites } from "./userFavourites";

const queryKey = ["auth"];

export interface Preferences {
  id: string;
  transactionVisibleColumns?: Record<string, ColumnMeta>;
  fillContainersVisibleColumns?: Record<string, ColumnMeta>;
}

export interface Favourites {
  id: string;
  sidebarFavourites: string[];
  selectedCustomer?: Partial<CustomerWithTimezoneListDto>;
  version: number;
}

export interface AppState {
  auth?: AuthorizeWebResponse;
  collapsed: boolean;
  menuOpened?: boolean;
  menuFocused?: boolean;
  isModalOpened: boolean;
  preferences?: Preferences;
  selectedCustomer?: Partial<CustomerListDto>;
}

export default function useApp() {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();

  function getData(): AppState | undefined {
    return queryClient.getQueryData(queryKey);
  }

  function setData(data: Partial<AppState>) {
    const appState: AppState | undefined = getData();
    queryClient.setQueryData(queryKey, { ...appState, ...data });
  }

  function toggleCollapsed(c?: boolean) {
    const appState = getData();
    setData({ collapsed: c ?? !appState?.collapsed });
  }

  function toggleMenuOpened(c?: boolean) {
    const appState = getData();
    setData({ menuOpened: c ?? !appState?.menuOpened });
  }

  function toggleModalOpened() {
    const appState = getData();
    setData({ isModalOpened: !appState?.isModalOpened });
  }

  function resetApp() {
    queryClient.clear();
  }

  function login(auth: AuthorizeWebResponse) {
    setData({
      auth,
      preferences: data?.preferences ? JSON.parse(auth?.preferences ?? "{}") : undefined,
    });

    if (auth.language) {
      void i18n.changeLanguage(auth.language);
    }
  }

  async function logout() {
    const lang = Intl.DateTimeFormat().resolvedOptions().locale;
    const language = resolveLanguage(lang as any);
    document.documentElement.lang = language;
    i18n.changeLanguage(language);

    // Clean up superset selection from local storage
    localStorage.removeItem("lastSelectedSuperset");

    // Dropping auth is what unmounts the authenticated views; App clears the cache once that
    // render has committed. Clearing it here instead would let those still-mounted views rebuild
    // their queries and fire them at the session that was just ended.
    setData({ auth: undefined });
  }

  const { data } = useQuery<{
    collapsed: boolean;
    auth?: AuthorizeWebResponse;
    isModalOpened: boolean;
    menuOpened?: boolean;
    menuFocused?: boolean;
    selectedCustomer?: Partial<CustomerWithTimezoneListDto>;
    preferences?: Preferences;
  }>({
    queryKey: queryKey,
    queryFn: () => new Promise<any>(() => queryClient.getQueryData(queryKey)),
    initialData: { collapsed: false, auth: undefined, isModalOpened: false, menuOpened: false },
  });

  const { favourites, isFavouritesFetching } = useFavourites({ enabled: !!data?.auth?.id });
  const selectedCustomer = favourites?.selectedCustomer;
  // A user with a single customer has no reason to open the topbar picker, so nothing ever writes
  // selectedCustomer. Feature flags and prefills can fall back to that one customer, but it must not
  // become the global selection: that scopes every table, hides customer filters and locks customer fields.
  const authCustomers = data?.auth?.features ?? [];
  const onlyCustomer: Partial<CustomerWithTimezoneListDto> | undefined =
    authCustomers.length === 1 ? authCustomers[0].customer : undefined;
  const defaultCustomer = selectedCustomer ?? onlyCustomer;

  return {
    login,
    appData: {
      ...data,
      selectedCustomer,
      defaultCustomer,
    },
    isFetching: isFavouritesFetching,
    onAfterLogout: logout,
    resetApp,
    toggleCollapsed,
    toggleMenuOpened,
    toggleModalOpened,
    setData,
  };
}
